/**
 * Tabla de listado de reglas de límites con toggle de estado.
 *
 * @taskId T478
 * @see functional/Specs/specs_cycle_4.md HU-28-FE §Pantalla 1
 */

import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Users } from 'lucide-react';
import {
  Badge,
  Button,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@flexibilitypay/ui';

import type { LimitRuleSummary } from '../types/limit.types';
import {
  formatMoney,
  SUBJECT_TYPE_LABELS,
  OPERATION_TYPE_LABELS,
  PERIOD_SCOPE_LABELS,
} from '../types/limit.types';

export interface LimitRulesTableProps {
  rules: LimitRuleSummary[];
  isLoading: boolean;
  onEdit: (rule: LimitRuleSummary) => void;
  onAssign: (rule: LimitRuleSummary) => void;
  onToggle: (rule: LimitRuleSummary, newValue: boolean) => void;
  onCreate?: () => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function LimitRulesTable({
  rules,
  isLoading,
  onEdit,
  onAssign,
  onToggle,
  onCreate,
  currentPage,
  totalPages,
  onPageChange,
}: LimitRulesTableProps) {
  const { t } = useTranslation(['limits', 'common']);

  const handleToggle = (rule: LimitRuleSummary) => {
    onToggle(rule, !rule.activo);
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre de la Regla</TableHead>
              <TableHead>Aplica A</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Período</TableHead>
              <TableHead className="text-right">Límite</TableHead>
              <TableHead className="text-right">Límite Normativo</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                <TableCell><Skeleton className="h-6 w-12" /></TableCell>
                <TableCell><Skeleton className="h-8 w-24" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Botón crear (solo cuando se provee onCreate) */}
      {onCreate && (
        <div className="flex justify-end">
          <Button onClick={onCreate}>
            <Plus className="mr-2 h-4 w-4" />
            {t('limits:rules.createNew', 'Crear Nuevo Límite')}
          </Button>
        </div>
      )}

      {/* Estado vacío */}
      {rules.length === 0 ? (
        <div className="rounded-md border p-8 text-center">
          <p className="text-muted-foreground">
            {t('limits:rules.empty', 'No hay reglas de límites configuradas.')}
          </p>
          <Button onClick={onCreate} className="mt-4">
            <Plus className="mr-2 h-4 w-4" />
            {t('limits:rules.createFirst', 'Crear primera regla')}
          </Button>
        </div>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('limits:rules.columns.nombre', 'Nombre de la Regla')}</TableHead>
                  <TableHead>{t('limits:rules.columns.scope', 'Aplica A')}</TableHead>
                  <TableHead>{t('limits:rules.columns.tipo', 'Tipo')}</TableHead>
                  <TableHead>{t('limits:rules.columns.periodo', 'Período')}</TableHead>
                  <TableHead className="text-right">{t('limits:rules.columns.limite', 'Límite')}</TableHead>
                  <TableHead className="text-right">{t('limits:rules.columns.normativo', 'Límite Normativo')}</TableHead>
                  <TableHead>{t('limits:rules.columns.estado', 'Estado')}</TableHead>
                  <TableHead className="text-right">{t('limits:rules.columns.acciones', 'Acciones')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell className="font-medium">{rule.nombre}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{SUBJECT_TYPE_LABELS[rule.aplicaA]}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {(OPERATION_TYPE_LABELS as Record<string, string>)[rule.tipoTransaccion] ?? rule.tipoTransaccion}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {PERIOD_SCOPE_LABELS[rule.periodo]}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatMoney(rule.monto)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">
                      {formatMoney(rule.limiteNormativo)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={rule.activo}
                          onCheckedChange={() => handleToggle(rule)}
                          aria-label={rule.nombre}
                        />
                        <span className="text-xs text-muted-foreground">
                          {rule.activo ? 'Activa' : 'Inactiva'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Editar"
                              onClick={() => onEdit(rule)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Editar regla</TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Asignar"
                              onClick={() => onAssign(rule)}
                            >
                              <Users className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Asignar merchants</TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 0}
              >
                Anterior
              </Button>
              <span className="text-sm text-muted-foreground">
                Página {currentPage + 1} de {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
              >
                Siguiente
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
